import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Post } from "@/lib/types";
import { PostHeader } from "@/components/PostHeader";
import { PostContent } from "@/components/PostContent";
import { PostActions } from "@/components/PostActions";
import { Comments } from "@/components/Comments";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { X, Heart, MessageCircle, Handshake, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CommentForm } from "@/components/CommentForm";
import { ShareButtons } from "./ShareButtons";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface PostDialogProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
}

export const PostDialog = ({ post, isOpen, onClose }: PostDialogProps) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [localPost, setLocalPost] = useState<Post>(post);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [hasSupported, setHasSupported] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);

  const uniqueComments = useMemo(() => {
    if (!localPost.comments) return [];
    
    const commentsMap = new Map();
    localPost.comments.forEach(comment => {
      if (!commentsMap.has(comment.id)) {
        commentsMap.set(comment.id, comment);
      }
    });
    
    return Array.from(commentsMap.values());
  }, [localPost.comments]);

  const validCategory = (localPost.category || 'general') as 'general' | 'gratitude' | 'support' | 'encouragement';

  const handleLike = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to like posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasLiked) {
        setLocalPost(prev => ({
          ...prev,
          likes_count: (prev.likes_count || 0) + 1
        }));
        setHasLiked(true);

        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: localPost.id,
            user_id: user.id,
            reaction_type: 'like'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Post liked! 💖",
          description: "Thanks for spreading the love!",
        });
      }
    } catch (error) {
      console.error('Error liking post:', error);
      setLocalPost(prev => ({
        ...prev,
        likes_count: (prev.likes_count || 0) - 1
      }));
      setHasLiked(false);
      toast({
        title: "Error",
        description: "Could not like the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSupport = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to support posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasSupported) {
        setHasSupported(true);

        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: localPost.id,
            user_id: user.id,
            reaction_type: 'support'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Support Given! 💜",
          description: "Your support means a lot to the community!",
        });
      }
    } catch (error) {
      console.error('Error supporting post:', error);
      setHasSupported(false);
      toast({
        title: "Error",
        description: "Could not support the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddComment = async (content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to comment",
          variant: "destructive"
        });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const newComment = {
        post_id: localPost.id,
        user_id: user.id,
        content,
        created_at: new Date().toISOString()
      };

      const { data: commentData, error } = await supabase
        .from('comments')
        .insert([newComment])
        .select()
        .single();

      if (error) throw error;

      setLocalPost(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          {
            id: commentData.id,
            content: content,
            user_id: user.id,
            created_at: commentData.created_at,
            profiles: {
              username: profile.username,
              avatar_url: ""
            }
          }
        ]
      }));

      toast({
        title: "Comment added! 💭",
        description: "Thanks for engaging in the conversation!",
      });

      setShowCommentForm(false);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Could not add comment. Please try again.",
        variant: "destructive"
      });
    }
  };

  const shareOptions = [
    { name: "Facebook", icon: "facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/post/${localPost.id}`)}` },
    { name: "Instagram", icon: "instagram", url: `https://www.instagram.com` },
    { name: "Twitter", icon: "twitter", url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this post: ${localPost.content.substring(0, 50)}...`)}&url=${encodeURIComponent(`${window.location.origin}/post/${localPost.id}`)}` },
    { name: "TikTok", icon: "tiktok", url: `https://www.tiktok.com` },
    { name: "WhatsApp", icon: "whatsapp", url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${localPost.content.substring(0, 50)}... ${window.location.origin}/post/${localPost.id}`)}` },
    { name: "Copy Link", icon: "link", url: "" }
  ];

  const handleShareOptionClick = (option: any) => {
    if (option.name === "Copy Link") {
      navigator.clipboard.writeText(`${window.location.origin}/post/${localPost.id}`);
      toast({
        description: "Link copied to clipboard!",
      });
      setShowShareSheet(false);
      return;
    }
    
    window.open(option.url, "_blank");
    setShowShareSheet(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className={`${isMobile ? 'w-full h-[100dvh] p-4 max-w-none m-0' : 'max-w-3xl p-6'} overflow-hidden bg-white dark:bg-gray-950 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800`}
          closeButton={null}
        >
          <DialogTitle className="flex justify-between items-center">
            <span className="sr-only">Post details</span>
            <DialogClose asChild>
              <Button 
                className="absolute right-4 top-4 hover:bg-gray-100 dark:hover:bg-gray-800" 
                variant="ghost" 
                size="icon"
                onClick={(e) => e.stopPropagation()}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>
          </DialogTitle>

          <ScrollArea className={`${isMobile ? 'h-[calc(100dvh-8rem)]' : 'h-[80vh]'}`}>
            <div className="space-y-6">
              <PostHeader
                username={localPost.profiles?.username || "Anonymous"}
                userId={localPost.user_id}
                category={validCategory}
                isAnonymous={localPost.is_anonymous}
                createdAt={localPost.created_at}
              />
              <PostContent
                content={localPost.content}
                imageUrl={localPost.image_url}
                videoUrl={localPost.video_url}
              />
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleLike()} 
                    className={`flex items-center gap-2 hover:scale-105 transition-transform ${hasLiked ? 'text-pink-500' : ''}`}
                    disabled={hasLiked}
                  >
                    <Heart className={`w-5 h-5 ${hasLiked ? 'fill-current' : ''}`} />
                    <span className="font-medium">{localPost.likes_count || 0}</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="flex items-center gap-2 hover:scale-105 transition-transform"
                    onClick={() => setShowCommentForm(!showCommentForm)}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-medium">{uniqueComments.length}</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`flex items-center gap-2 hover:scale-105 transition-transform ${hasSupported ? 'text-purple-500' : ''}`}
                    onClick={() => handleSupport()}
                    disabled={hasSupported}
                  >
                    <Handshake className="w-5 h-5" fill={hasSupported ? "currentColor" : "none"} />
                    <span className="font-medium">Support</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 hover:scale-105 transition-transform"
                    onClick={() => setShowShareSheet(true)}
                  >
                    <Share2 className="w-5 h-5" />
                    <span className="font-medium">Share</span>
                  </Button>
                </div>
              </div>
              
              {showCommentForm && (
                <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
                  <CommentForm
                    onSubmit={handleAddComment}
                    placeholder="Write a comment..."
                    buttonText="Comment"
                    postId={localPost.id || ''}
                    postContent={localPost.content}
                    postCategory={localPost.category}
                  />
                </div>
              )}
              
              {uniqueComments.length > 0 && (
                <div className="mt-4 pt-2 border-t border-gray-200 dark:border-gray-800">
                  <h3 className="font-medium text-lg mb-4">Comments</h3>
                  <Comments
                    comments={uniqueComments.map(comment => ({
                      id: comment.id,
                      username: comment.profiles?.username || "Anonymous",
                      userId: comment.user_id,
                      content: comment.content,
                      createdAt: comment.created_at,
                      postId: localPost.id || '',
                    }))}
                    onAddComment={handleAddComment}
                    onAddReaction={() => {}}
                    postId={localPost.id || ''}
                    postContent={localPost.content}
                    postCategory={localPost.category}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Sheet open={showShareSheet} onOpenChange={setShowShareSheet}>
        <SheetContent className="w-full sm:max-w-md">
          <div className="px-4 py-6">
            <h2 className="text-lg font-semibold mb-6">Share this post</h2>
            
            <div className="grid grid-cols-3 gap-6">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  className="flex flex-col items-center justify-center gap-2 hover:opacity-80 transition-opacity"
                  onClick={() => handleShareOptionClick(option)}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                    {option.icon === "facebook" && (
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9.19795 21.5H13.198V13.4901H16.8021L17.198 9.50977H13.198V7.5C13.198 6.94772 13.6457 6.5 14.198 6.5H17.198V2.5H14.198C11.4365 2.5 9.19795 4.73858 9.19795 7.5V9.50977H7.19795L6.80206 13.4901H9.19795V21.5Z"></path>
                      </svg>
                    )}
                    {option.icon === "instagram" && (
                      <svg className="w-6 h-6 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C14.717 2 15.056 2.01 16.122 2.06C17.187 2.11 17.912 2.277 18.55 2.525C19.21 2.779 19.766 3.123 20.322 3.678C20.8305 4.1779 21.224 4.78259 21.475 5.45C21.722 6.087 21.89 6.813 21.94 7.878C21.987 8.944 22 9.283 22 12C22 14.717 21.99 15.056 21.94 16.122C21.89 17.187 21.722 17.912 21.475 18.55C21.2247 19.2178 20.8311 19.8226 20.322 20.322C19.822 20.8303 19.2173 21.2238 18.55 21.475C17.913 21.722 17.187 21.89 16.122 21.94C15.056 21.987 14.717 22 12 22C9.283 22 8.944 21.99 7.878 21.94C6.813 21.89 6.088 21.722 5.45 21.475C4.78233 21.2245 4.17753 20.8309 3.678 20.322C3.16941 19.8222 2.77593 19.2175 2.525 18.55C2.277 17.913 2.11 17.187 2.06 16.122C2.013 15.056 2 14.717 2 12C2 9.283 2.01 8.944 2.06 7.878C2.11 6.812 2.277 6.088 2.525 5.45C2.77524 4.78218 3.1688 4.17732 3.678 3.678C4.17767 3.16923 4.78243 2.77573 5.45 2.525C6.088 2.277 6.812 2.11 7.878 2.06C8.944 2.013 9.283 2 12 2ZM12 7C10.6739 7 9.40215 7.52678 8.46447 8.46447C7.52678 9.40215 7 10.6739 7 12C7 13.3261 7.52678 14.5979 8.46447 15.5355C9.40215 16.4732 10.6739 17 12 17C13.3261 17 14.5979 16.4732 15.5355 15.5355C16.4732 14.5979 17 13.3261 17 12C17 10.6739 16.4732 9.40215 15.5355 8.46447C14.5979 7.52678 13.3261 7 12 7V7Z"></path>
                      </svg>
                    )}
                    {option.icon === "twitter" && (
                      <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M22.162 5.656C21.3986 5.9937 20.589 6.21548 19.76 6.314C20.6337 5.79144 21.2877 4.96902 21.6 4C20.78 4.488 19.881 4.83 18.944 5.015C18.3146 4.34158 17.4804 3.89497 16.5709 3.74459C15.6615 3.59421 14.7279 3.74849 13.9153 4.18346C13.1026 4.61842 12.4564 5.30969 12.0771 6.1498C11.6978 6.9899 11.6068 7.93178 11.818 8.829C10.1551 8.74566 8.52832 8.31353 7.04328 7.56067C5.55823 6.80781 4.24812 5.75105 3.19799 4.459C2.82628 5.09745 2.63095 5.82323 2.63199 6.562C2.63199 8.012 3.36999 9.293 4.49199 10.043C3.828 10.0221 3.17862 9.84278 2.59799 9.52V9.572C2.59819 10.5377 2.93236 11.4736 3.54384 12.2211C4.15532 12.9685 5.00647 13.4815 5.95299 13.673C5.33661 13.84 4.6903 13.8647 4.06299 13.745C4.32986 14.5762 4.85 15.3031 5.55058 15.8241C6.25117 16.345 7.09712 16.6338 7.96999 16.65C7.10247 17.3313 6.10917 17.835 5.04687 18.1322C3.98458 18.4294 2.87412 18.5143 1.77899 18.382C3.69069 19.6114 5.91609 20.2641 8.18899 20.262C15.882 20.262 20.089 13.889 20.089 8.362C20.089 8.182 20.084 8 20.076 7.822C20.8949 7.23017 21.6016 6.49702 22.163 5.657L22.162 5.656Z"></path>
                      </svg>
                    )}
                    {option.icon === "tiktok" && (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"></path>
                      </svg>
                    )}
                    {option.icon === "whatsapp" && (
                      <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path>
                      </svg>
                    )}
                    {option.icon === "link" && (
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm">{option.name}</span>
                </button>
              ))}
            </div>
            
            <div className="mt-8">
              <Button
                className="w-full"
                variant="outline"
                onClick={() => setShowShareSheet(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
